import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EluveNameLogo,
  Heading,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@eluve/components';
import { locationUsersFragment } from '@eluve/frontend-feature-location-hierarchy';
import type { SupportedVendors } from '@eluve/graphql-types';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled, useTenantName } from '@eluve/smart-blocks';
import { SUPPORT_URL_DATA_PROTECTION_POLICIES } from '@eluve/utils';
import { getVendorDataMapper } from '@eluve/vendor';

import { useEhrOnboardingStatus } from '../hooks/useEhrOnboardingStatus';
import { useImportDataFromEhr } from '../useImportDataFromEhr';

// --- Shared dialog wrapper ---

const OnboardingDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, className = 'px-24 pb-24 pt-32', children }) => (
  <Dialog
    open={isOpen}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent size="xl" className="flex flex-col gap-0 p-6">
      <DialogHeader>
        <DialogTitle>
          <EluveNameLogo className="h-7.5" />
        </DialogTitle>
      </DialogHeader>
      <div className={`h-128 w-full items-center ${className}`}>{children}</div>
    </DialogContent>
  </Dialog>
);

// --- Sub-components ---

const ImportedAppointmentsPrompt: React.FC = () => {
  const [_, setSearchParams] = useSearchParams();
  return (
    <div>
      <div className="pt-24">
        <Stack direction="horizontal" align="center">
          <Icon
            name="CircleCheck"
            size="3xl"
            className="mr-1 border-none text-white"
          />
          <Heading>You're all set!</Heading>
        </Stack>
        <div className="mt-4">
          <span className="text-center text-sm">
            Your EHR integration is all set up! You can now see your upcoming
            appointments on the homepage.
          </span>
        </div>

        <div className="pt-10">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setSearchParams({ event: '' });
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImportingAppointmentsPrompt: React.FC<{
  firstEhr: { vendor: SupportedVendors } | null;
}> = ({ firstEhr }) => {
  if (!firstEhr) {
    return null;
  }
  const vendorProvider = getVendorDataMapper(firstEhr.vendor);
  const logoData = vendorProvider.getLogo();
  const logo = <img src={logoData} alt="Logo" className="w-20" />;
  return (
    <div>
      <Stack className="mb-10" align="center">
        {logo}
      </Stack>
      <div>
        <Heading>Importing appointments</Heading>
        <div className="mt-2">
          <span className="text-sm">
            It takes a few seconds for the first import, but afterwards we will
            automatically sync your EHR.
          </span>
        </div>
        <Stack align="center">
          <Stack
            className="py-6"
            direction="horizontal"
            align="center"
            fullWidth={false}
          >
            <Icon name="RefreshCw" size="lg" className="mr-2 animate-spin" />
            <span className="text-sm">Importing...</span>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};

const OnboardingEhrImport: React.FC<{
  firstEhr: { vendor: SupportedVendors } | null;
}> = ({ firstEhr }) => {
  const tenantId = useTenantIdFromParams();
  const {
    importDataFromEhr,
    isImportAllowed,
    isDataImporting,
    isSupportedBrowser,
    importResults,
  } = useImportDataFromEhr(tenantId!);
  let logo: React.JSX.Element | null = null;
  if (firstEhr) {
    const vendorProvider = getVendorDataMapper(firstEhr.vendor);
    const logoData = vendorProvider.getLogo();
    logo = <img src={logoData} alt="Logo" className="w-20" />;
  }

  useCaptureEventOnMount('ehr_onboarding_import_onboarding_shown');

  const importAppointmentsPrompt = (
    <div>
      <Stack className="mb-10" align="center">
        {logo}
      </Stack>
      <div>
        <Heading>Import appointments</Heading>
        <div className="mt-2">
          <span className="text-sm">
            Import EHR data so that Eluve can work seamlessly with your workflow
          </span>
        </div>
        {importResults?.error ? (
          <Stack
            className="my-3 self-center bg-bg-warning-subtle p-1"
            direction="horizontal"
            align="stretch"
          >
            <Text className="font-light md:text-xs">{importResults.error}</Text>
          </Stack>
        ) : (
          <div className="p-4"></div>
        )}
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => {
            importDataFromEhr({ showToast: false });
          }}
          disabled={!isImportAllowed && !isSupportedBrowser}
        >
          Start Import
        </Button>
        <Stack
          className="mt-8 h-11 cursor-pointer bg-bg-tertiary p-3"
          onClick={() => {
            window.open(
              SUPPORT_URL_DATA_PROTECTION_POLICIES,
              '_blank',
              'noopener,noreferrer',
            );
          }}
          direction="horizontal"
          align="center"
        >
          <Icon name="CircleHelp" size="sm" className="mr-2" />
          <span className="text-xs font-light">
            Learn how Eluve keeps your data safe
          </span>
        </Stack>
      </div>
    </div>
  );

  return match({ isDataImporting, importResults })
    .with({ isDataImporting: true }, () => (
      <ImportingAppointmentsPrompt firstEhr={firstEhr} />
    ))
    .with({ importResults: { ok: true } }, () => <ImportedAppointmentsPrompt />)
    .otherwise(() => importAppointmentsPrompt);
};

const OnboardingLocation: React.FC<{
  locations: Array<{ id: string; name: string }>;
  onLocationAdded: () => Promise<void>;
}> = ({ locations, onLocationAdded }) => {
  const [newLocation, setNewLocation] = useState<string | null>(null);
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();
  const tenantName = useTenantName(tenantId!);
  const apiClient = useApiClient();

  useCaptureEventOnMount('ehr_onboarding_location_onboarding_shown');

  const defaultLocation = locations.length === 1 ? locations[0]!.id : undefined;
  const newLocationId = newLocation ?? defaultLocation;

  const addUserToLocation = async () => {
    await apiClient.locations.addUserToLocation({
      params: {
        locationId: newLocationId!,
        tenantId: tenantId!,
      },
    });
    cacheUtils.updateFragment(
      {
        fragment: locationUsersFragment,
        key: {
          id: newLocationId!,
        },
      },
      (existing) => {
        if (!existing) {
          return existing;
        }
        const newUser = {
          __typename: 'LocationUsers' as const,
          userId,
          locationId: newLocationId!,
          isAutoAssigned: false,
          user: {
            __typename: 'Users' as const,
            id: userId,
            email: '',
            firstName: '',
            lastName: '',
          },
        };
        return {
          ...existing,
          location_users: [...existing.location_users, newUser],
        };
      },
    );
  };

  const placeholder = `Select your ${tenantName ?? ''} location`;
  return (
    <div>
      <Heading>Extension installed!</Heading>
      <div>
        <span className="text-sm">Connect your EHR account with Eluve</span>
      </div>
      <div className="py-8">
        <Select onValueChange={setNewLocation} value={defaultLocation ?? ''}>
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {!locations.length && (
              <Stack
                className="bg-bg-warning-subtle p-4 text-text-warning-subtle"
                align="center"
                gap={2}
                justify="center"
              >
                <Icon name="TriangleAlert" size="xl" />
                No locations found
                <span className="text-sm">
                  Please contact organization admin to setup a location
                </span>
              </Stack>
            )}
            {locations.map((location) => {
              return (
                <SelectItem
                  key={location.id}
                  className="pl-7 focus:bg-bg-accent-quaternary"
                  value={location.id}
                >
                  {location.name}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="secondary"
        className="w-full"
        onClick={async () => {
          await addUserToLocation();
          await onLocationAdded();
        }}
        disabled={!newLocationId}
      >
        Next
      </Button>
    </div>
  );
};

// --- Main component ---

export const EhrOnboarding: React.FC = () => {
  const isEhrOnboardingEnabled = useIsFeatureFlagEnabled('EHR_ONBOARDING');
  const tenantId = useTenantIdFromParams();
  const { isImportAllowed, isSupportedBrowser } = useImportDataFromEhr(
    tenantId!,
  );
  const { data: onboardingStatus, refetch } = useEhrOnboardingStatus();

  const firstEhr = onboardingStatus.externalEhrs[0] ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const isUserOnboarding = searchParams.get('event') === 'onboarding';
  const closeOnboarding = () => setSearchParams({ event: '' });

  return match({
    isEhrOnboardingEnabled,
    isUserOnboarding,
    isUserPartOfAnyLocation: onboardingStatus.isUserPartOfAnyLocation,
    hasSyncedBefore: onboardingStatus.hasSyncedBefore,
    isImportAllowed,
    isSupportedBrowser,
  })
    .with({ isEhrOnboardingEnabled: false }, () => null)
    .with(
      {
        isUserOnboarding: false,
        isImportAllowed: false,
        isSupportedBrowser: false,
      },
      () => null,
    )
    .with({ isUserPartOfAnyLocation: false }, () => (
      <OnboardingDialog isOpen={isUserOnboarding} onClose={closeOnboarding}>
        <OnboardingLocation
          locations={onboardingStatus.locations}
          onLocationAdded={refetch}
        />
      </OnboardingDialog>
    ))
    .with({ hasSyncedBefore: false }, () => (
      <OnboardingDialog
        isOpen={isUserOnboarding}
        onClose={closeOnboarding}
        className="self-center px-24 pt-10 pb-20"
      >
        <OnboardingEhrImport firstEhr={firstEhr} />
      </OnboardingDialog>
    ))
    .otherwise(() => null);
};
